import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import {
  globalMarkets,
  tamilNaduDistricts,
  indiaCities,
  getPublishedLocations
} from '../../data/markets/marketRegistry.js';
import { useModal } from '../../contexts/ModalContext';

const LocationsHubPage = () => {
  const navigate = useNavigate();
  const { openConsultation } = useModal();
  const [activeTab, setActiveTab] = useState('tamil-nadu');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const publishedLocations = getPublishedLocations();
  const publishedMap = new Map(publishedLocations.map(loc => [loc.id, loc]));

  const seo = {
    metaTitle: "Locations & Regional Coverage | Byte Pairs Software Studio",
    metaDescription: "Byte Pairs is headquartered in Dharapuram, Tamil Nadu, engineering custom software, billing systems, and web portals for businesses across India and international markets.",
    keywords: "Byte Pairs locations, software company Dharapuram, custom software Tamil Nadu, software development India, international software engineering, IT services coverage",
  };

  // Filter datasets
  const query = searchQuery.toLowerCase().trim();

  const filteredTnDistricts = tamilNaduDistricts.filter(d => {
    if (!query) return true;
    return (
      d.district.toLowerCase().includes(query) ||
      d.representativeCity.toLowerCase().includes(query) ||
      d.targetAudience.toLowerCase().includes(query)
    );
  });

  const filteredIndiaCities = indiaCities.filter(c => {
    if (!query) return true;
    return (
      c.name.toLowerCase().includes(query) ||
      c.state.toLowerCase().includes(query) ||
      c.targetAudience.toLowerCase().includes(query)
    );
  });

  const filteredGlobalMarkets = globalMarkets.filter(m => {
    if (!query) return true;
    return (
      m.name.toLowerCase().includes(query) ||
      m.targetIndustries.toLowerCase().includes(query) ||
      m.tier.toLowerCase().includes(query)
    );
  });

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": seo.metaTitle,
    "description": seo.metaDescription,
    "url": "https://bytepairs.com/locations",
    "about": {
      "@type": "LocalBusiness",
      "name": "Byte Pairs",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Hanumandapuram",
        "addressLocality": "Dharapuram",
        "addressRegion": "Tamil Nadu",
        "postalCode": "638656",
        "addressCountry": "IN"
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>{seo.metaTitle}</title>
        <meta name="description" content={seo.metaDescription} />
        <meta name="keywords" content={seo.keywords} />
        <link rel="canonical" href="https://bytepairs.com/locations" />
        <meta property="og:title" content={seo.metaTitle} />
        <meta property="og:description" content={seo.metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://bytepairs.com/locations" />

        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      <Header />

      <main className="min-h-screen bg-background text-foreground pt-24 pb-20">
        {/* Hero Section */}
        <section className="relative overflow-hidden py-14 lg:py-20 border-b border-border/50">
          <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent pointer-events-none" />
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold mb-4">
                <Icon name="MapPin" size={14} />
                <span>Headquarters: Dharapuram, Tamil Nadu</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight text-foreground mb-6">
                Direct Engineering From <span className="text-primary">Dharapuram</span> to Across the Globe
              </h1>
              <p className="text-base sm:text-lg text-text-secondary leading-relaxed mb-8">
                Byte Pairs operates with complete transparency. We are physically rooted in Dharapuram, Tamil Nadu, developing custom ERPs, billing applications, and web platforms for regional enterprises, national hubs, and international clients in over 20 countries.
              </p>
              <div className="flex flex-wrap items-center gap-4 text-sm font-medium text-text-secondary">
                <div className="flex items-center space-x-2 bg-card px-3.5 py-2 rounded-lg border border-border">
                  <Icon name="CheckCircle" size={16} className="text-primary" />
                  <span>Pay Once, Own Forever</span>
                </div>
                <div className="flex items-center space-x-2 bg-card px-3.5 py-2 rounded-lg border border-border">
                  <Icon name="CheckCircle" size={16} className="text-primary" />
                  <span>Direct Founder Access</span>
                </div>
                <div className="flex items-center space-x-2 bg-card px-3.5 py-2 rounded-lg border border-border">
                  <Icon name="CheckCircle" size={16} className="text-primary" />
                  <span>No SaaS Subscription Bloat</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Search & Navigation Bar */}
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-6 border-b border-border/60">
            {/* Tabs */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setActiveTab('tamil-nadu')}
                className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center space-x-2 ${
                  activeTab === 'tamil-nadu'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-card text-text-secondary hover:text-foreground border border-border hover:border-primary/40'
                }`}
              >
                <Icon name="Compass" size={16} />
                <span>Tamil Nadu Districts & Hubs ({tamilNaduDistricts.length})</span>
              </button>
              <button
                onClick={() => setActiveTab('india-cities')}
                className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center space-x-2 ${
                  activeTab === 'india-cities'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-card text-text-secondary hover:text-foreground border border-border hover:border-primary/40'
                }`}
              >
                <Icon name="Building" size={16} />
                <span>India Metros & Commercial Cities ({indiaCities.length})</span>
              </button>
              <button
                onClick={() => setActiveTab('global-markets')}
                className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center space-x-2 ${
                  activeTab === 'global-markets'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-card text-text-secondary hover:text-foreground border border-border hover:border-primary/40'
                }`}
              >
                <Icon name="Globe" size={16} />
                <span>International Markets ({globalMarkets.length})</span>
              </button>
            </div>

            {/* Search Input */}
            <div className="relative w-full md:w-80">
              <Icon
                name="Search"
                size={16}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-secondary"
              />
              <input
                type="text"
                placeholder="Search market, district, or industry..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground focus:outline-none focus:border-primary transition-colors"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-secondary hover:text-foreground"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Market Cards Grid */}
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          {/* TAB 1: TAMIL NADU */}
          {activeTab === 'tamil-nadu' && (
            <div className="space-y-6">
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-5 mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h3 className="font-bold text-base text-foreground mb-1">
                    Tamil Nadu Regional Footprint (All 38 Districts)
                  </h3>
                  <p className="text-sm text-text-secondary">
                    From our headquarters in Dharapuram, we serve industrial clusters across Tiruppur (knitwear exports), Coimbatore (machinery & pumps), Erode (textiles & turmeric), Karur (home textiles), and Chennai (technology & enterprise).
                  </p>
                </div>
                <Link
                  to="/software-development-company-dharapuram"
                  className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-semibold text-xs shrink-0 hover:opacity-90 transition-opacity"
                >
                  <span>Explore Dharapuram HQ</span>
                  <Icon name="ArrowRight" size={14} />
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTnDistricts.map((item) => {
                  const isPublished = publishedMap.has(item.id);
                  const pubData = publishedMap.get(item.id);
                  const isHQ = item.id === 'dharapuram';

                  return (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`relative bg-card rounded-xl p-6 border transition-all duration-300 hover:shadow-lg flex flex-col justify-between ${
                        isHQ
                          ? 'border-primary ring-2 ring-primary/20 bg-gradient-to-br from-card via-card to-primary/5'
                          : isPublished
                          ? 'border-primary/40 hover:border-primary'
                          : 'border-border/70 hover:border-border'
                      }`}
                    >
                      <div>
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div>
                            <span className="text-xs font-semibold text-primary uppercase tracking-wider block mb-0.5">
                              {item.district} District
                            </span>
                            <h3 className="text-lg font-bold text-foreground">
                              {item.representativeCity}
                            </h3>
                          </div>
                          {isHQ ? (
                            <span className="px-2.5 py-1 rounded-full bg-primary text-primary-foreground text-xs font-bold shrink-0">
                              Official HQ
                            </span>
                          ) : isPublished ? (
                            <span className="px-2 py-0.5 rounded-md bg-success/10 text-success text-xs font-semibold border border-success/30 shrink-0">
                              Active Hub
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-md bg-secondary/80 text-text-secondary text-xs font-medium shrink-0">
                              Coverage Area
                            </span>
                          )}
                        </div>

                        <p className="text-xs text-text-secondary line-clamp-3 mb-4 leading-relaxed">
                          {isPublished
                            ? pubData.tagline || pubData.overview
                            : item.targetAudience}
                        </p>
                      </div>

                      <div className="pt-4 border-t border-border/50 mt-2">
                        {isPublished ? (
                          <Link
                            to={`/software-development-company-${item.id}`}
                            className="inline-flex items-center space-x-1.5 text-sm font-semibold text-primary hover:underline group"
                          >
                            <span>View {item.representativeCity} Hub</span>
                            <Icon name="ArrowRight" size={14} className="group-hover:translate-x-1 transition-transform" />
                          </Link>
                        ) : (
                          <div className="flex items-center justify-between text-xs text-text-secondary">
                            <span>Service coverage available</span>
                            <button
                              onClick={openConsultation}
                              className="text-primary hover:underline font-semibold"
                            >
                              Consult Team
                            </button>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

          {/* TAB 2: INDIA CITIES */}
          {activeTab === 'india-cities' && (
            <div className="space-y-6">
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-5 mb-8">
                <h3 className="font-bold text-base text-foreground mb-1">
                  National Commercial & Technology Hubs
                </h3>
                <p className="text-sm text-text-secondary">
                  We build custom software, web platforms, and mobile applications for startups, manufacturing firms, and retail brands across India. Our direct developer model ensures fast delivery without agency markups.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredIndiaCities.slice(0, 60).map((city) => {
                  const isPublished = publishedMap.has(city.id);
                  const pubData = publishedMap.get(city.id);

                  return (
                    <motion.div
                      key={city.id + city.state}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`bg-card rounded-xl p-6 border transition-all duration-300 hover:shadow-lg flex flex-col justify-between ${
                        isPublished ? 'border-primary/50' : 'border-border/70'
                      }`}
                    >
                      <div>
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div>
                            <span className="text-xs text-text-secondary block">
                              {city.state}
                            </span>
                            <h3 className="text-lg font-bold text-foreground">
                              {city.name}
                            </h3>
                          </div>
                          {isPublished ? (
                            <span className="px-2 py-0.5 rounded-md bg-success/10 text-success text-xs font-semibold border border-success/30">
                              Active Hub
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-md bg-secondary text-text-secondary text-xs">
                              {city.tier}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-text-secondary line-clamp-2 mb-4 leading-relaxed">
                          {isPublished ? pubData.tagline : (city.targetAudience || 'Custom software, portals and business automations.')}
                        </p>
                      </div>

                      <div className="pt-4 border-t border-border/50 mt-2">
                        {isPublished ? (
                          <Link
                            to={`/software-development-company-${city.id}`}
                            className="inline-flex items-center space-x-1.5 text-sm font-semibold text-primary hover:underline group"
                          >
                            <span>Explore {city.name}</span>
                            <Icon name="ArrowRight" size={14} className="group-hover:translate-x-1 transition-transform" />
                          </Link>
                        ) : (
                          <div className="flex items-center justify-between text-xs text-text-secondary">
                            <span>Remote engineering available</span>
                            <button
                              onClick={openConsultation}
                              className="text-primary hover:underline font-semibold"
                            >
                              Enquire
                            </button>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {filteredIndiaCities.length > 60 && (
                <div className="text-center py-6 text-sm text-text-secondary">
                  Showing top 60 of {filteredIndiaCities.length} commercial cities. Use the search bar above to locate any specific Indian city or town.
                </div>
              )}
            </div>
          )}

          {/* TAB 3: GLOBAL MARKETS */}
          {activeTab === 'global-markets' && (
            <div className="space-y-6">
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-5 mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h3 className="font-bold text-base text-foreground mb-1">
                    International Software Engineering (20+ Target Markets)
                  </h3>
                  <p className="text-sm text-text-secondary">
                    Serving international businesses with dedicated regional presence in Kenya (East Africa) led by Nancy Karimi, along with Tier A delivery in the US, UK, UAE, Singapore, and Australia.
                  </p>
                </div>
                <Link
                  to="/global-collaboration"
                  className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-semibold text-xs shrink-0 hover:opacity-90 transition-opacity"
                >
                  <span>Meet International Team</span>
                  <Icon name="ArrowRight" size={14} />
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredGlobalMarkets.map((market) => {
                  const isPublished = publishedMap.has(market.id);
                  const pubData = publishedMap.get(market.id);

                  return (
                    <motion.div
                      key={market.id}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`bg-card rounded-xl p-6 border transition-all duration-300 hover:shadow-lg flex flex-col justify-between ${
                        isPublished ? 'border-primary ring-1 ring-primary/20' : 'border-border/70'
                      }`}
                    >
                      <div>
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div>
                            <span className="text-xs text-text-secondary block font-mono">
                              ISO: {market.isoAlpha2 || market.isoAlpha3}
                            </span>
                            <h3 className="text-lg font-bold text-foreground">
                              {market.name}
                            </h3>
                          </div>
                          {isPublished ? (
                            <span className="px-2 py-0.5 rounded-md bg-success/10 text-success text-xs font-semibold border border-success/30">
                              Active International Hub
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-md bg-secondary text-text-secondary text-xs">
                              {market.tier}
                            </span>
                          )}
                        </div>

                        <p className="text-xs text-text-secondary line-clamp-2 mb-4 leading-relaxed">
                          {isPublished
                            ? pubData.tagline || pubData.overview
                            : (market.targetIndustries || 'Enterprise software outsourcing, custom web portals, and automation.')}
                        </p>
                      </div>

                      <div className="pt-4 border-t border-border/50 mt-2">
                        {isPublished ? (
                          <Link
                            to={`/software-development-company-${market.id}`}
                            className="inline-flex items-center space-x-1.5 text-sm font-semibold text-primary hover:underline group"
                          >
                            <span>Explore {market.name} Hub</span>
                            <Icon name="ArrowRight" size={14} className="group-hover:translate-x-1 transition-transform" />
                          </Link>
                        ) : (
                          <div className="flex items-center justify-between text-xs text-text-secondary">
                            <span>Global Delivery Available</span>
                            <button
                              onClick={openConsultation}
                              className="text-primary hover:underline font-semibold"
                            >
                              Enquire
                            </button>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {/* CTA Banner */}
        <section className="max-w-7xl mx-auto px-6 lg:px-8 mt-12">
          <div className="bg-gradient-to-r from-card via-card to-primary/10 border border-primary/20 rounded-2xl p-8 lg:p-12 text-center max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-black text-foreground mb-4">
              Need a Custom Software Solution in Your Market?
            </h2>
            <p className="text-text-secondary text-sm sm:text-base max-w-2xl mx-auto mb-8 leading-relaxed">
              Whether you are an established manufacturer in Tiruppur, a retailer in Dharapuram, or an enterprise in Kenya or the US, we build custom digital systems tailored to your exact workflows. Pay once, own the code forever.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button variant="default" size="lg" onClick={openConsultation}>
                Book a Direct Founder Consultation
              </Button>
              <Link to="/contact-engagement-hub">
                <Button variant="outline" size="lg">
                  Visit Contact Hub
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default LocationsHubPage;
